import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Sparkles, Rocket, ArrowRight, TrendingUp } from 'lucide-react';

const ThankYou = () => {
  const [countdown, setCountdown] = useState(10);
  const [redirectFailed, setRedirectFailed] = useState(false);
  
  const username = localStorage.getItem('username') || 'Benutzer';

  // Direkt zum Dashboard weiterleiten
  const goToDashboard = () => {
    localStorage.removeItem('showThankYou');
    // Versuche mehrere Methoden
    try {
      window.location.replace('/dashboard');
    } catch (e) {
      window.location.href = '/dashboard';
    }
  };

  useEffect(() => {
    let countdownInterval;
    let redirectTimeout;
    
    // Countdown
    countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Automatische Weiterleitung nach 10 Sekunden
    redirectTimeout = setTimeout(() => {
      goToDashboard();
      // Falls redirect nicht geklappt hat, zeige Fallback nach 2 weiteren Sekunden
      setTimeout(() => {
        setRedirectFailed(true);
      }, 2000);
    }, 10000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimeout);
    };
  }, []);

  const handleUpsellClick = () => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Landing Page Funnel',
        value: 49.00,
        currency: 'EUR'
      });
    }
    window.open('https://app.we-love-leads.com/payment-link/682372dfde176768278265d2', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full space-y-6">
        {/* Success Message */}
        <Card className="shadow-2xl border-4 border-green-500 bg-white">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              🎉 Willkommen bei FinAds Creator!
            </h1>
            <p className="text-xl text-gray-700 mb-2">
              Hallo <span className="font-bold text-amber-600">{username}</span>! 
            </p>
            <p className="text-lg text-gray-600 mb-4">
              Dein Account wurde erfolgreich erstellt.
            </p>
            
            {/* Großer, sichtbarer Button zum Dashboard */}
            <div className="mt-6">
              {countdown > 0 && !redirectFailed ? (
                <p className="text-gray-500 mb-3">
                  Automatische Weiterleitung in {countdown} Sekunden...
                </p>
              ) : (
                <p className="text-red-500 mb-3 font-semibold">
                  Klicke auf den Button um fortzufahren:
                </p>
              )}
              <Button
                onClick={goToDashboard}
                className="h-14 px-8 text-lg font-bold bg-green-600 hover:bg-green-700 text-white"
              >
                ➡️ Jetzt zum Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upsell Offer */}
        <Card className="shadow-2xl border-4 border-amber-500 relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-500 to-amber-600"></div>
          
          <CardHeader className="bg-gradient-to-br from-amber-50 to-slate-50 pb-6">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Rocket className="w-8 h-8 text-amber-600" />
              <CardTitle className="text-3xl text-center text-slate-900">
                🚀 Exklusives Einführungsangebot!
              </CardTitle>
            </div>
            <p className="text-center text-gray-600 text-lg">
              Starte jetzt mit einem individuellen Landing Page Funnel durch
            </p>
          </CardHeader>

          <CardContent className="pt-8 pb-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  Dein maßgeschneiderter Landing Page Funnel
                </h3>
                <p className="text-gray-700 mb-6">
                  Perfekt abgestimmt auf deine Finanzprodukte und Zielgruppe:
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Conversion-optimierte Landing Page</strong> speziell für deine Produkte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Integration mit FinAds Creator</strong> - Deine Ads leiten direkt hierhin</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Facebook Pixel & CAPI</strong> vorinstalliert für perfektes Tracking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Lead-Formulare</strong> zur direkten Kundengewinnung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Mobil-optimiert</strong> für alle Geräte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong>Eigene Domain</strong> inklusive (Wert: 15€/Monat)</span>
                  </li>
                </ul>

                <div className="bg-gradient-to-r from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-300">
                  <p className="text-sm text-gray-700">
                    <strong>🎁 Bonus:</strong> Kostenlose Einrichtung im Wert von 500€!
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-amber-300">
                  <div className="text-center mb-6">
                    <div className="inline-block px-4 py-2 bg-red-500 text-white rounded-full text-sm font-bold mb-4 animate-pulse">
                      🔥 Nur für Neukunden
                    </div>
                    <p className="text-gray-600 line-through text-xl mb-2">Normalpreis: 149€/Monat</p>
                    <div className="text-6xl font-bold text-slate-900 mb-2">49€</div>
                    <p className="text-gray-600 text-lg">pro Monat</p>
                    <p className="text-sm text-green-600 font-semibold mt-2">Spare 100€/Monat!</p>
                  </div>

                  <Button
                    onClick={handleUpsellClick}
                    className="w-full h-16 text-xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900"
                  >
                    <Sparkles className="w-6 h-6 mr-2" />
                    Jetzt sichern für 49€/Monat
                  </Button>

                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-3">Monatlich kündbar • Sofort einsatzbereit</p>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span>Erhöht deine Conversion-Rate um durchschnittlich 40%</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={goToDashboard}
                  variant="ghost"
                  className="mt-4 w-full text-white hover:text-amber-400"
                >
                  Nein danke, weiter zum Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-gray-400 text-sm">
            ✓ Von über 500+ Finanzdienstleistern genutzt • ✓ DSGVO-konform • ✓ Made in Germany
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
